import { Observable } from 'rxjs';
import { Library, LibraryMetrics } from '../../model/library/library.model';
import { LibraryQueryParams } from '../../model/library/library-query-params.model';
import { PolymorphicLibrary } from '../../model/library/library-types.model';
import { LibraryForEdit } from '../../model/library/library-edit.model';
import { UpdateLibraryCommand } from '../../model/library/commands/library-update-command';

// Re-export for backward compatibility
export type { LibraryQueryParams } from '../../model/library/library-query-params.model';

/**
 * Paginated response structure
 */
export interface PaginatedLibraries {
  libraries: Library[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Abstract repository for Library domain entity
 * Defines the contract for accessing library data
 *
 * Implementation should be provided in infrastructure layer
 * following the Dependency Inversion Principle
 */
export abstract class LibraryRepository {
  /**
   * Get paginated list of libraries with optional filters
   * @param params Query parameters for pagination, search and filtering
   * @returns Observable of paginated libraries
   */
  abstract getLibraries(
    params?: LibraryQueryParams
  ): Observable<PaginatedLibraries>;

  /**
   * Get dashboard metrics for libraries
   * @param date Optional date (defaults to today)
   * @returns Observable of library metrics
   */
  abstract getMetrics(date?: Date | string): Observable<LibraryMetrics>;

  /**
   * Get all libraries with CREATED status for a specific cell
   * These are libraries available for sequencing runs
   * @param cellId The cell UUID to filter libraries by
   * @returns Observable of polymorphic libraries array
   */
  abstract getLibrariesByCellIdCreated(
    cellId: string
  ): Observable<PolymorphicLibrary[]>;

  /**
   * Get all libraries with CREATED status for a specific technology
   * Used for Microarreglos runs where libraries are loaded by technology instead of cell
   * @param technology The technology to filter libraries by (e.g., 'MICROARREGLOS')
   * @returns Observable of polymorphic libraries array
   */
  abstract getLibrariesByTechnologyCreated(
    technology: string
  ): Observable<PolymorphicLibrary[]>;

  /**
   * Get library details for edit screen
   * @param libraryId The library UUID
   * @returns Observable of library with specimens
   */
  abstract getLibraryForEdit(libraryId: string): Observable<LibraryForEdit>;

  /**
   * Update library specimens
   * @param command Update command (typed by technology)
   * @returns Observable of updated library
   */
  abstract updateLibrarySpecimens(
    command: UpdateLibraryCommand
  ): Observable<LibraryForEdit>;

  /**
   * Disable a library (HU40)
   * Changes library status from CREATED to DISABLED and releases all specimens.
   * @param libraryId The library UUID
   * @returns Observable that completes on success
   */
  abstract disableLibrary(libraryId: string): Observable<void>;
}

/**
 * @deprecated Use LibraryQueryParams from domain/model/library/library-query-params.model instead
 */
export type LibreriaQueryParams = LibraryQueryParams;

/**
 * @deprecated Use PaginatedLibraries instead
 */
export type PaginatedLibrerias = PaginatedLibraries;

/**
 * @deprecated Use LibraryRepository instead
 */
export abstract class LibreriaRepository extends LibraryRepository {}
